import { chatService } from '../../services/chat-service.js';
import { loadConfig } from '../../config/environment.js';

export interface ChatConnectionOptions {
  source: string;
}

export interface ChatConnectionCallbacks {
  onConnected: () => void;
  onDisconnected: () => void;
  onOffline?: () => void;
  onMessage: (content: string, data?: any) => void;
  onTyping: (isTyping: boolean) => void;
  onFinished: () => void;
  onFinishedAll?: () => void;
  onError: (message?: string) => void;
}

export class ChatConnection {
  constructor(
    private readonly options: ChatConnectionOptions,
    private readonly callbacks: ChatConnectionCallbacks
  ) { }

  private readonly handleOffline = (): void => {
    chatService.pauseReconnection();
    this.callbacks.onOffline?.();
  };

  private readonly handleOnline = (): void => {
    if (chatService.intentionallyClosed) {
      return;
    }

    chatService.resumeReconnection();
    // Reconnect remains user-driven (input/button click).
    if (!chatService.connected) {
    }
  };

  async connect(): Promise<boolean> {
    try {
      await loadConfig();

      const connected = await chatService.createAndConnectWithBFF(
        this.options.source,
        {
          reconnect: {
            maxAttempts: 3,
            baseDelayMs: 1000,
            maxDelayMs: 10000,
          },
        }
      );

      if (connected) {
        this.setupNetworkListeners();
        this.setupWebSocketListeners();
        this.callbacks.onConnected();
      } else {
        // Scenario: Initial connection failure (BFF did not respond, timeout, etc)
        this.callbacks.onError('Failed to establish connection');
        this.callbacks.onDisconnected();
      }

      return connected;
    } catch (error) {
      // Scenario: Exception during connection attempt (network, BFF offline, unexpected error)
      const errorMessage = error instanceof Error ? error.message : 'Connection failed';
      this.callbacks.onError(errorMessage);
      this.callbacks.onDisconnected();
      return false;
    }
  }

  /**
   * Attempts to reconnect an existing session via BFF.
   * Returns true if reconnection succeeded and WebSocket is open.
   */
  async reconnect(): Promise<boolean> {
    try {
      const connected = await chatService.reconnect();

      if (connected) {
        this.setupNetworkListeners();
        this.setupWebSocketListeners();
        this.callbacks.onConnected();
        return true;
      } else {
        return false;
      }
    } catch (error) {
      return false;
    }
  }

  /**
    * Reconnects using reconnectSecret + chatId from sessionStorage.
   * Hydrates the SessionManager before calling the normal reconnect flow.
   */
  async reconnectFromStorage(): Promise<boolean> {
    try {
      await loadConfig();

      const hydrated = chatService.hydrateFromStorage();
      if (!hydrated) return false;

      return this.reconnect();
    } catch (error) {
      return false;
    }
  }

  disconnect() {
    this.removeNetworkListeners();
    chatService.disconnect();
    this.callbacks.onDisconnected();
  }

  private setupNetworkListeners(): void {
    // Remove before adding to prevent double-registration on reconnect
    globalThis.removeEventListener('offline', this.handleOffline);
    globalThis.removeEventListener('online', this.handleOnline);
    globalThis.addEventListener('offline', this.handleOffline);
    globalThis.addEventListener('online', this.handleOnline);
  }

  private removeNetworkListeners(): void {
    globalThis.removeEventListener('offline', this.handleOffline);
    globalThis.removeEventListener('online', this.handleOnline);
  }

  private setupWebSocketListeners() {
    chatService.clearHandlers();

    chatService.onMessage((data: unknown) => {
      const messageData = data as any;
      const content = typeof messageData?.data === 'string' ? messageData.data : '';
      this.callbacks.onMessage(content, messageData);
    });

    chatService.onTyping((data: unknown) => {
      const typingData = data as { isTyping?: boolean };
      this.callbacks.onTyping(typingData.isTyping || false);
    });

    chatService.onFinished(() => {
      this.callbacks.onFinished();
    });

    chatService.onFinishedAll(() => {
      this.callbacks.onFinishedAll?.();
    });

    chatService.onError((data: unknown) => {
      const errorData = data as { message?: string };
      const errorMessage = errorData.message || 'Connection error occurred';
      // Scenario: Error notified by ChatService (WebSocket error, parse error, etc)
      this.callbacks.onError(errorMessage);
    });

    chatService.onDisconnect(() => {
      // Scenario: WebSocket closed — update connection state
      this.callbacks.onDisconnected();
    });
  }
}
